import {
  AssessmentStateV2,
  ConfidenceV2,
  EngagementAssessmentV2Run,
  PILLARS_V2,
  PillarResultV2,
  PillarV2,
  SINS_V2,
  SinV2,
  V2SinResult,
} from "../../../../types/engagementAssessmentV2Types";

export const STATE_SCORE: Record<AssessmentStateV2, number> = {
  STRONG: 100,
  ADEQUATE: 75,
  MIXED: 50,
  WEAK: 25,
  NOT_RATABLE: 0,
};

export const CONFIDENCE_PCT: Record<ConfidenceV2, number> = {
  HIGH: 90,
  MEDIUM: 65,
  LOW: 35,
};

export const stateMeetsGateway = (state: AssessmentStateV2): boolean =>
  state === "STRONG" || state === "ADEQUATE";

export const titleCase = (s: string): string =>
  s
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

export type PillarRow = {
  index: number;
  pillar: PillarV2;
  label: string;
  state: AssessmentStateV2;
  score: number;
  judgement: string;
  confidence: ConfidenceV2 | null;
  confidencePct: number | null;
  evidenceRefs: string[];
  isPresent: boolean;
};

export type SinRow = {
  index: number;
  sin: SinV2;
  label: string;
  isFlagged: boolean;
  judgement: string;
  confidence: ConfidenceV2 | null;
  evidenceRefs: string[];
};

export type RadarDatum = { pillar: string; score: number };

export type AssessmentViewModel = {
  pillarRows: PillarRow[];
  sinRows: SinRow[];
  flaggedSins: SinRow[];
  radarData: RadarDatum[];
  healthIndex: number;
  pillarsMet: number;
  pillarsTotal: number;
  sinsFlagged: number;
  sinsTotal: number;
  peakPillar: PillarRow | null;
  weakestPillar: PillarRow | null;
  expectations: EngagementAssessmentV2Run["assessment"]["expectations"];
};

export const buildPillarRows = (pillars: PillarResultV2[]): PillarRow[] => {
  const byKey = new Map<PillarV2, PillarResultV2>(
    pillars.map((p) => [p.pillar, p]),
  );
  return PILLARS_V2.map((pillar, i) => {
    const entry = byKey.get(pillar);
    const state: AssessmentStateV2 = entry?.state ?? "NOT_RATABLE";
    return {
      index: i + 1,
      pillar,
      label: titleCase(pillar),
      state,
      score: STATE_SCORE[state],
      judgement:
        entry?.judgement ?? "No result returned for this pillar in this run.",
      confidence: entry?.confidence ?? null,
      confidencePct: entry ? CONFIDENCE_PCT[entry.confidence] : null,
      evidenceRefs: entry?.evidence_refs ?? [],
      isPresent: entry !== undefined,
    };
  });
};

export const buildSinRows = (sins: V2SinResult[]): SinRow[] => {
  const byKey = new Map<SinV2, V2SinResult>(sins.map((s) => [s.sin, s]));
  return SINS_V2.map((sin, i) => {
    const entry = byKey.get(sin);
    return {
      index: i + 1,
      sin,
      label: titleCase(sin),
      isFlagged: entry !== undefined,
      judgement: entry?.judgement ?? "No issues detected for this criterion.",
      confidence: entry?.confidence ?? null,
      evidenceRefs: entry?.evidence_refs ?? [],
    };
  });
};

export const buildViewModel = (
  run: EngagementAssessmentV2Run,
): AssessmentViewModel => {
  const pillarRows = buildPillarRows(run.assessment.pillars);
  const sinRows = buildSinRows(run.assessment.sins);
  const flaggedSins = sinRows.filter((s) => s.isFlagged);
  const radarData = pillarRows.map((p) => ({
    pillar: p.label,
    score: p.score,
  }));
  const ratable = pillarRows.filter((p) => p.state !== "NOT_RATABLE");
  const healthIndex =
    ratable.length > 0
      ? Math.round(
          ratable.reduce((sum, p) => sum + p.score, 0) / ratable.length,
        )
      : 0;
  const sorted = [...pillarRows].sort((a, b) => b.score - a.score);
  return {
    pillarRows,
    sinRows,
    flaggedSins,
    radarData,
    healthIndex,
    pillarsMet: pillarRows.filter((p) => stateMeetsGateway(p.state)).length,
    pillarsTotal: PILLARS_V2.length,
    sinsFlagged: flaggedSins.length,
    sinsTotal: SINS_V2.length,
    peakPillar: sorted[0] ?? null,
    weakestPillar: sorted[sorted.length - 1] ?? null,
    expectations: run.assessment.expectations,
  };
};
